import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  TextField,
} from "@mui/material";

export interface SimpleMultiSelectFilterProps {
  label: string;
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  minWidth?: number;
  maxWidth?: number;
  showSearch?: boolean;
}

const SimpleMultiSelectFilter: React.FC<SimpleMultiSelectFilterProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  minWidth = 150,
  maxWidth,
  showSearch = false,
}) => {
  const [searchText, setSearchText] = useState("");
  const labelId = `${label.toLowerCase().replace(/\s+/g, "-")}-filter-label`;

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [options, searchText],
  );

  const handleSelectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([...filteredOptions]);
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <FormControl
      size="small"
      sx={{
        minWidth,
        maxWidth,
        "& .MuiInputBase-root": { fontSize: "0.8rem", height: 32 },
        "& .MuiInputLabel-root": { fontSize: "0.8rem" },
      }}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        multiple
        value={selectedValues}
        label={label}
        onChange={(e) =>
          onChange(
            typeof e.target.value === "string"
              ? e.target.value.split(",")
              : e.target.value,
          )
        }
        renderValue={(selected) => (
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selected.length === 0 ? "All" : selected.join(", ")}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 350,
              "& .MuiMenuItem-root": { fontSize: "0.8rem", py: 0.25 },
            },
          },
        }}
        onClose={() => setSearchText("")}
      >
        {/* Search Box - only shown if showSearch is true */}
        {showSearch && (
          <Box
            sx={{ px: 1, py: 0.5, borderBottom: "1px solid #e0e0e0" }}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <TextField
              size="small"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fullWidth
              autoFocus
              sx={{
                "& .MuiInputBase-root": { fontSize: "0.8rem", height: 28 },
              }}
            />
          </Box>
        )}

        {/* Select All / Clear All Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 1,
            py: 0.5,
            borderBottom: "1px solid #e0e0e0",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Button
            size="small"
            onClick={handleSelectAll}
            sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={handleClearAll}
            sx={{ fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
          >
            Clear All
          </Button>
        </Box>

        {/* Options */}
        {filteredOptions.map((option) => (
          <MenuItem key={option} value={option} sx={{ fontSize: "0.8rem" }}>
            <Checkbox
              checked={selectedValues.indexOf(option) > -1}
              size="small"
            />
            <ListItemText
              primary={option}
              primaryTypographyProps={{ fontSize: "0.8rem" }}
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default SimpleMultiSelectFilter;
